import { Link } from "react-router-dom";
import { PenLine, FlaskConical, Sparkles, GraduationCap, ArrowUpRight, ArrowLeft, BookOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { useLanguage } from "@/i18n/LanguageContext";
import { TOPICS, ARTICLES, BLOG_URL } from "@/data/blog";

const ICONS = { pen: PenLine, flask: FlaskConical, sparkles: Sparkles, book: GraduationCap };

export default function BeyondPage() {
  const { lang, t } = useLanguage();

  return (
    <div className="grain min-h-screen bg-void text-white">
      <Seo
        title={lang === "it" ? "Oltre il codice — scrittura, scienza e magia | Fabio Comelli" : "Beyond the code — writing, science and magic | Fabio Comelli"}
        path="/oltre"
        description={lang === "it"
          ? "Cosa faccio oltre a sviluppare app: scrittura, divulgazione scientifica, magia e insegnamento. Articoli scelti dal mio blog La Sveglia e il Risveglio."
          : "What I do besides building apps: writing, science communication, magic and teaching. Selected articles from my blog La Sveglia e il Risveglio."}
      />
      <Navbar />
      <main className="px-6 pt-32 pb-28 md:px-12" data-testid="beyond-page">
        <div className="mx-auto max-w-7xl">
          <Link to="/" data-testid="beyond-back-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2 transition-colors duration-300 hover:text-neon">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {t.privacy.back}
          </Link>

          <Reveal className="mt-12">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon">{t.beyond.kicker}</p>
            <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl lg:text-6xl" data-testid="beyond-title">
              {t.beyond.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted2 md:text-lg">{t.beyond.sub}</p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted2 md:text-lg" data-testid="beyond-history">{t.beyond.history}</p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TOPICS.map((topic, i) => {
              const Icon = ICONS[topic.icon];
              return (
                <Reveal key={topic.icon} delay={i * 0.1}>
                  <div className="h-full border bg-surface p-8 transition-transform duration-300 hover:-translate-y-1.5" style={{ borderColor: `${topic.color}45` }} data-testid={`beyond-topic-${topic.icon}`}>
                    <Icon className="h-6 w-6" style={{ color: topic.color }} />
                    <h2 className="mt-6 font-display text-xl font-bold uppercase tracking-tight">{topic.title[lang]}</h2>
                    <p className="mt-4 text-sm leading-relaxed text-muted2">{topic.desc[lang]}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-24">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight" data-testid="beyond-articles-title">{t.beyond.articlesTitle}</h2>
              <a
                href={BLOG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="beyond-blog-link"
                className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 hover:border-neon hover:text-neon"
              >
                <BookOpen className="h-4 w-4" />
                {t.beyond.blogCta}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-8">
            <ul className="border-t border-white/10" data-testid="beyond-articles">
              {ARTICLES.map((a, i) => (
                <li key={a.url}>
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`beyond-article-${i}`}
                    className="group flex items-center gap-4 border-b border-white/10 py-5 transition-colors duration-300 hover:bg-white/[0.02] md:gap-6"
                  >
                    <span className="font-mono text-xs text-muted2">{a.year}</span>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TOPICS[a.tag].color }} />
                    <span className="flex-1 font-display text-base font-bold tracking-tight transition-colors duration-300 group-hover:text-white md:text-xl">
                      {a.title[lang]}
                    </span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-muted2 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-neon" />
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
